/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Journal Ledger Service - SuperAdmin Journal & Ledger Audit API
 * ============================================================================
 */

import { get } from '@/services/api/client';
import { ApiResponse, PagedResponse } from '@/services/api/types';

const BASE_URL = '/admin/journal';

// ── Types ───────────────────────────────────────────────────────────────────

export interface JournalLineDto {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debitAmount: number;
  creditAmount: number;
  currency: string;
  description: string;
}

export interface JournalEntryDto {
  id: string;
  entryNumber: string;
  entryDate: string;
  description: string;
  sourceType: string;
  sourceId: string | null;
  correlationId: string | null;
  totalDebit: number;
  totalCredit: number;
  status: string;
  reversedById: string | null;
  reversesId: string | null;
  createdAt: string;
  lines?: JournalLineDto[];
}

export interface AccountBalance {
  accountId: string;
  code: string;
  name: string;
  accountType: string;
  totalDebit: number;
  totalCredit: number;
  netBalance: number;
}

export interface TrialBalanceDto {
  asOf: string;
  grandTotalDebit: number;
  grandTotalCredit: number;
  balanced: boolean;
  accounts: AccountBalance[];
}

export interface DiscrepancyDto {
  accountId: string;
  accountCode: string;
  accountType: string;
  referenceId: string;
  journalBalance: number;
  entityBalance: number;
  difference: number;
}

export interface LedgerAccountDto {
  id: string;
  accountType: string;
  referenceId: string | null;
  code: string;
  name: string;
  active: boolean;
  createdAt: string;
}

export interface LedgerHealthDto {
  totalOrgsWithEntries: number;
  balancedOrgs: number;
  imbalancedOrgs: number;
  allBalanced: boolean;
  imbalancedOrgIds: string[];
}

// ── Service ─────────────────────────────────────────────────────────────────

export const journalLedgerService = {
  async getEntries(
    orgId: string,
    page = 0,
    size = 20,
    sourceType?: string
  ): Promise<PagedResponse<JournalEntryDto>> {
    const params: Record<string, unknown> = { orgId, page, size };
    if (sourceType) params.sourceType = sourceType;
    const response = await get<ApiResponse<PagedResponse<JournalEntryDto>>>(
      `${BASE_URL}/entries`,
      params
    );
    return response.data;
  },

  async getEntry(id: string, orgId: string): Promise<JournalEntryDto> {
    const response = await get<ApiResponse<JournalEntryDto>>(
      `${BASE_URL}/entries/${id}`,
      { orgId }
    );
    return response.data;
  },

  async getCorrelatedEntries(
    correlationId: string,
    orgId: string
  ): Promise<JournalEntryDto[]> {
    const response = await get<ApiResponse<JournalEntryDto[]>>(
      `${BASE_URL}/entries/correlation/${correlationId}`,
      { orgId }
    );
    return response.data;
  },

  async getTrialBalance(orgId: string, asOf?: string): Promise<TrialBalanceDto> {
    const params: Record<string, unknown> = { orgId };
    if (asOf) params.asOf = asOf;
    const response = await get<ApiResponse<TrialBalanceDto>>(
      `${BASE_URL}/trial-balance`,
      params
    );
    return response.data;
  },

  async verifyBalances(orgId: string): Promise<DiscrepancyDto[]> {
    const response = await get<ApiResponse<DiscrepancyDto[]>>(
      `${BASE_URL}/verify`,
      { orgId }
    );
    return response.data;
  },

  async isBalanced(orgId: string): Promise<boolean> {
    const response = await get<ApiResponse<boolean>>(
      `${BASE_URL}/balanced`,
      { orgId }
    );
    return response.data;
  },

  async getAccounts(orgId: string): Promise<LedgerAccountDto[]> {
    const response = await get<ApiResponse<LedgerAccountDto[]>>(
      `${BASE_URL}/accounts`,
      { orgId }
    );
    return response.data;
  },

  async getLedgerHealth(): Promise<LedgerHealthDto> {
    const response = await get<ApiResponse<LedgerHealthDto>>(
      `${BASE_URL}/health`
    );
    return response.data;
  },
};

export default journalLedgerService;
